import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  ISoccerMatch,
  ISoccerMatchListItem,
  SoccerMatchStatus,
} from "../../../types";
import { getTeamInfo } from "./useTeamLogos";
import { appController } from "../stores";

export const useMatchResult = ({
  soccerMatch,
}: {
  soccerMatch: ISoccerMatch | ISoccerMatchListItem;
}) => {
  const storeMatchData = appController.getState("matchData");
  const { t } = useTranslation();
  return useMemo(() => {
    const isMatchPlaying = soccerMatch.status === SoccerMatchStatus.PLAYING;
    const isMatchEnded = soccerMatch?.status === SoccerMatchStatus.PLAYED;
    const isMatchNotStarted = !isMatchPlaying && !isMatchEnded;
    const isPenaltyShootout = !!soccerMatch?.scores?.pen;
    // Stats Perform omits zero-value fields - at 0-0 the whole `scores`
    // object can be absent. For a playing/ended match the truthful score
    // is 0; upcoming matches keep undefined so no score renders.
    const liveDefault = isMatchPlaying || isMatchEnded ? 0 : undefined;
    const scoreboard = {
      home: soccerMatch.scores
        ? isPenaltyShootout
          ? soccerMatch.scores?.et?.home
          : soccerMatch.scores?.total?.home ?? 0
        : storeMatchData?.scores?.total?.home ?? liveDefault,
      away: soccerMatch.scores
        ? isPenaltyShootout
          ? soccerMatch.scores?.et?.away
          : soccerMatch.scores?.total?.away ?? 0
        : storeMatchData?.scores?.total?.away ?? liveDefault,
    };
    const isMatchDraw =
      isMatchEnded && !isPenaltyShootout && scoreboard.home === scoreboard.away;
    const penaltyScoreboard = {
      home: soccerMatch.scores?.pen?.home,
      away: soccerMatch.scores?.pen?.away,
    };
    const winnerTeam =
      isMatchDraw || !isMatchEnded
        ? null
        : soccerMatch.scores?.total?.home > soccerMatch.scores?.total?.away
        ? soccerMatch.contestants[0]
        : soccerMatch.contestants[1];

    const teamInfo = getTeamInfo(winnerTeam?.name ?? "");

    let matchInfoSummary = "";
    if (isMatchEnded) {
      if (isMatchDraw) {
        matchInfoSummary = t("matchResult.matchDraw");
      } else if (isPenaltyShootout) {
        matchInfoSummary = t("matchResult.winOnPenalties", {
          teamName: teamInfo.translatedName,
          winnerScore: Math.max(penaltyScoreboard.home, penaltyScoreboard.away),
          loserScore: Math.min(penaltyScoreboard.home, penaltyScoreboard.away),
        });
      } else {
        matchInfoSummary = t("matchResult.winFullTime", {
          teamName: teamInfo.translatedName ?? winnerTeam?.officialName,
        });
      }
    }

    return {
      isMatchPlaying,
      isMatchEnded,
      isMatchNotStarted,
      isMatchDraw,
      isPenaltyShootout,
      winnerTeam,
      finalScoreboard: scoreboard,
      penaltyScoreboard,
      matchInfoSummary,
    };
  }, [soccerMatch]);
};
